import {
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@apollo/orbit';
import React from 'react';
import { SelectChangeEvent } from 'typings/events/selection-change-event';

import { InsightsLineChartTooltip } from 'src/app/graph/insights/InsightsLineChartTooltip';
import { SeriesControls } from 'src/components/charts/helpers/SeriesControls';
import { LineChart } from 'src/components/charts/LineChart';
import { Resolution } from 'src/lib/graphqlTypes/types';

import { RequestsOverTimeHeading } from './RequestsOverTimeHeading';
import { useServiceReportLineCharts } from './ServiceReportLineCharts';
import { OperationMetricType } from './types';

interface Props {
  isSubscriptionOperation: boolean;
  requestsLineChartStackInfo: ReturnType<
    typeof useServiceReportLineCharts
  >['requestsLineChartStackInfo'];
  resolution: Resolution;
  operationMetricType: OperationMetricType;
  onOperationMetricTypeChange: (
    event: SelectChangeEvent<OperationMetricType>,
  ) => void;
}

const getChartDescription = ({
  metricType,
  isSubscriptionOperation,
}: {
  metricType: OperationMetricType;
  isSubscriptionOperation: boolean;
}) => {
  if (metricType === 'requestRate') {
    return isSubscriptionOperation
      ? 'The number of registrations per minute.'
      : 'The number of requests made per minute.';
  } else {
    return isSubscriptionOperation
      ? 'The number of times this subscription has been registered.'
      : 'The number of times this operation has been requested.';
  }
};

export function RequestsOverTime({
  isSubscriptionOperation,
  requestsLineChartStackInfo,
  resolution,
  operationMetricType,
  onOperationMetricTypeChange,
}: Props) {
  return (
    <Card variant="unstyled">
      <CardHeader>
        <CardTitle>
          <RequestsOverTimeHeading
            operationMetricType={operationMetricType}
            onOperationMetricTypeChange={onOperationMetricTypeChange}
            isSubscriptionOperation={isSubscriptionOperation}
          />
        </CardTitle>
        <CardDescription className="pl-3">
          {getChartDescription({
            metricType: operationMetricType,
            isSubscriptionOperation,
          })}
        </CardDescription>
      </CardHeader>
      <CardBody>
        <LineChart
          {...requestsLineChartStackInfo}
          stacked
          className="h-36"
          strokeWidth={1}
          resolution={resolution}
          data-testid="requests-line-chart"
          tooltipLabelAs={
            <InsightsLineChartTooltip
              title={
                operationMetricType === 'requestRate'
                  ? 'Request Rate'
                  : undefined
              }
            />
          }
          hideCursorArrow
        />
        <SeriesControls
          className="w-full px-4 pt-6 text-center"
          hideWhenSingleStack
          {...requestsLineChartStackInfo}
        />
      </CardBody>
    </Card>
  );
}
